import { useEffect, useCallback, useState, useRef } from 'react';
import { BASE_URL } from '../../constants/endpoints';
import { DASHBOARD_PATTERN } from '../../constants/patterns';
import { STORAGE_KEYS } from '../../constants/storage';
import { useInvalidLinks } from '../../context/InvalidLinksContext';

// Create a static map to track pending requests
// This is outside the component to persist across renders
const pendingValidations = new Map<string, Promise<boolean>>();

const CustomLink = ({ href, children, ...props }: React.ComponentPropsWithoutRef<'a'>) => {
  // Initially set to null (validating) for dashboard links, or true for other links
  const [isValidDashboard, setIsValidDashboard] = useState<boolean | null>(
    href && DASHBOARD_PATTERN.test(href) ? null : true
  );
  const { addInvalidLink } = useInvalidLinks();

  // Function to check if URL is internal
  const isInternalLink = useCallback((url: string) => {
    try {
      // Get the base URL of the parent website
      const parentOrigin = window.location.origin;
      const linkUrl = new URL(url, window.location.href);
      return linkUrl.origin === parentOrigin;
    } catch {
      return false;
    }
  }, []);

  // Extract account and appliance IDs from dashboard URL
  const extractIdsFromUrl = useCallback((url: string) => {
    const accountMatch = url.match(/\/dashboard\/(\d+)\//);
    const applianceMatch = url.match(/\/instances\/([^/]+)\//);
    
    return {
      accountId: accountMatch?.[1] || null,
      applianceId: applianceMatch?.[1] || null
    };
  }, []);

  // Create a key for the validation request
  const createValidationKey = useCallback((accountId: string, applianceId: string) => {
    return `${accountId}:${applianceId}`;
  }, []);

  // Validate dashboard URL
  const validateDashboardUrl = useCallback(async (url: string) => {
    if (!DASHBOARD_PATTERN.test(url)) {
      return false;
    }

    const { accountId, applianceId } = extractIdsFromUrl(url);
    if (!accountId || !applianceId) {
      return false;
    }

    const validationKey = createValidationKey(accountId, applianceId);

    // Check if there's already a pending request for this validation
    if (pendingValidations.has(validationKey)) {
      // Return the existing promise to avoid duplicate requests
      return pendingValidations.get(validationKey)!;
    }

    // Create a new validation promise
    const validationPromise = (async () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.WIDGET_AUTH_TOKEN);
        if (!token) {
          console.error('No auth token found');
          return false;
        }

        const response = await fetch(
          `${BASE_URL}/api/v1/accounts/validate_account_appliance/?account=${accountId}&appliance=${applianceId}`,
          {
            headers: {
              'Accept': '*/*',
              'Authorization': `Bearer ${token}`,
            }
          }
        );

        if (response.ok) {
          const result = await response.text();
          return result.toLowerCase() === 'true';
        }
        return false;
      } catch (error) {
        console.error('Error validating dashboard URL:', error);
        return false;
      } finally {
        // Remove this request from pending validations when done
        pendingValidations.delete(validationKey);
      }
    })();

    // Store the promise in our map
    pendingValidations.set(validationKey, validationPromise);
    
    // Return the promise
    return validationPromise;
  }, [extractIdsFromUrl, createValidationKey]);

  // Validate URL when href changes
  useEffect(() => {
    if (href) {
      if (DASHBOARD_PATTERN.test(href)) {
        // Set to null while validating to indicate it's in progress
        setIsValidDashboard(null);
        validateDashboardUrl(href).then(isValid => {
          setIsValidDashboard(isValid);
          if (!isValid) {
            // Add to invalid links context if not valid
            addInvalidLink(href);
          }
        });
      } else {
        // Links that don't need validation are immediately valid
        setIsValidDashboard(true);
      }
    }
  }, [href, validateDashboardUrl, addInvalidLink]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const url = href;
    if (!url) return;

    if (!isInternalLink(url)) {
      return; // Let default behavior handle external links
    }

    e.preventDefault();
    try {
      // Use History API to update URL without page reload
      window.history.pushState({}, '', url);
      
      // Dispatch a custom event that the parent site can listen to
      const navigationEvent = new CustomEvent('appmanager-ai-navigation', {
        // remove domain, only keep path
        detail: { url: new URL(url).pathname },
        bubbles: true,
      });
      window.dispatchEvent(navigationEvent);
    } catch (error) {
      console.error('Navigation failed:', error);
      // Fallback to default behavior
      window.open(url, '_self');
    }
  }, [href, isInternalLink]);

  // If it's still validating or if it's invalid, render as plain text
  if (isValidDashboard === null || (isValidDashboard === false && DASHBOARD_PATTERN.test(href || ''))) {
    return <span className={isValidDashboard === null ? "validating-link" : "invalid-link"}>{children}</span>;
  }

  return (
    <a 
      href={href}
      onClick={handleClick}
      // Only open in new tab for external links
      {...(!isInternalLink(href || '') ? {
        target: "_blank",
        rel: "noopener noreferrer"
      } : {})}
      {...props}
    >
      {children}
    </a>
  );
};

export default CustomLink;